import { DEFAULT_MAX_RESPONDENTS } from "@digital-twins/shared-types";
import type { StudyType } from "@/lib/study-type";

export interface EffectiveRespondentLimits {
  min: number;
  max: number;
}

function resolvePoolSize(
  filteredCount: number | null,
  personaCount?: number | null,
): number | null {
  if (filteredCount != null && Number.isFinite(filteredCount)) {
    const floored = Math.floor(filteredCount);
    if (floored >= 0) {
      return floored;
    }
    return null;
  }
  if (
    typeof personaCount === "number" &&
    Number.isFinite(personaCount) &&
    personaCount > 0
  ) {
    return Math.floor(personaCount);
  }
  return null;
}

/**
 * Caps respondent min/max against the usable audience pool (filtered count or
 * population persona count), keeping the study type's bounds when the pool is
 * unknown. The number offered is `DEFAULT_PLANNED_SAMPLE_SIZE` (or a design's
 * target), clamped to these bounds by the creation state.
 */
export function getEffectiveRespondentLimits(
  studyType: Pick<StudyType, "respondentLimits">,
  poolInput: {
    filteredCount: number | null;
    personaCount?: number | null;
    /**
     * Design-specific ceiling. Every design shares DEFAULT_MAX_RESPONDENTS.
     * (TRUE_MONADIC_RETIRED removed the raised split-sample cap), so this is a
     * pass-through that replaces the study type's max before the pool cap is
     * applied.
     */
    designMax?: number | null;
  },
): EffectiveRespondentLimits {
  const base = studyType.respondentLimits ?? {};
  const baseMin = base.min ?? 30;
  const baseMax = poolInput.designMax ?? base.max ?? DEFAULT_MAX_RESPONDENTS;

  const P = resolvePoolSize(poolInput.filteredCount, poolInput.personaCount);
  if (P === null) {
    return { min: baseMin, max: baseMax };
  }

  const M = Math.min(baseMax, P);
  const rawMin = M < baseMin ? Math.min(baseMin, M) : baseMin;
  const min = Math.min(rawMin, M);

  return { min, max: M };
}
